import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RelationshipType } from "@/types/dashboard";
import { Heart, Users, User, Briefcase, UserRound, CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

// Helper function to get the relationship icon
const getRelationshipIcon = (type?: RelationshipType) => {
  if (!type) return null;
  
  switch (type) {
    case "Spouse":
    case "Partner":
      return <Heart className="h-4 w-4 text-rose-500" />;
    case "Parent":
    case "Sibling":
    case "Family":
      return <Users className="h-4 w-4 text-indigo-500" />;
    case "Friend":
      return <UserRound className="h-4 w-4 text-emerald-500" />;
    case "Colleague":
    case "Client":
    case "Mentor":
    case "Professional":
      return <Briefcase className="h-4 w-4 text-amber-500" />;
    default:
      return <User className="h-4 w-4 text-gray-500" />;
  }
};

interface FirstContactFormProps {
  onComplete: (contactData: {
    name: string;
    relationshipType: RelationshipType;
    interests: string[];
    note?: string;
    weddingDate?: Date;
    marriageAnniversary?: Date;
    spousePreferences?: string;
    loveLanguage?: string;
    firstDate?: Date;
    anniversaryDate?: Date;
    anniversaryType?: string;
  }) => void;
}

const FirstContactForm: React.FC<FirstContactFormProps> = ({ onComplete }) => {
  const [name, setName] = useState("");
  const [relationshipType, setRelationshipType] = useState<RelationshipType | "">("");
  const [interests, setInterests] = useState("");
  const [note, setNote] = useState("");
  
  // Spouse-specific fields
  const [weddingDate, setWeddingDate] = useState<Date | undefined>();
  const [marriageAnniversary, setMarriageAnniversary] = useState<Date | undefined>();
  const [spousePreferences, setSpousePreferences] = useState("");
  const [loveLanguage, setLoveLanguage] = useState("");
  
  // Partner-specific fields  
  const [firstDate, setFirstDate] = useState<Date | undefined>();
  const [anniversaryDate, setAnniversaryDate] = useState<Date | undefined>();
  const [anniversaryType, setAnniversaryType] = useState("");
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !relationshipType) return;
    
    const interestsList = interests
      .split(',')
      .map(interest => interest.trim())
      .filter(interest => interest.length > 0);
    
    onComplete({
      name,
      relationshipType: relationshipType as RelationshipType,
      interests: interestsList,
      note: note.trim().length > 0 ? note : undefined,
      // Spouse fields
      weddingDate,
      marriageAnniversary,
      spousePreferences: spousePreferences.trim().length > 0 ? spousePreferences : undefined,
      loveLanguage: loveLanguage.length > 0 ? loveLanguage : undefined,
      // Partner fields
      firstDate,
      anniversaryDate,
      anniversaryType: anniversaryType.length > 0 ? anniversaryType : undefined,
    });
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="contact-name">Who's someone you don't want to forget?</Label>
        <Input
          id="contact-name"
          placeholder="Enter name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="relationship-type">How do you know them?</Label>
        <Select
          value={relationshipType}
          onValueChange={(value) => setRelationshipType(value as RelationshipType)}
          required
        >
          <SelectTrigger id="relationship-type" className="w-full">
            <SelectValue placeholder="Select relationship">
              {relationshipType && (
                <div className="flex items-center gap-2">
                  {getRelationshipIcon(relationshipType as RelationshipType)}
                  <span>{relationshipType}</span>
                </div>
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Spouse">
              <div className="flex items-center gap-2">
                <Heart className="h-4 w-4 text-rose-500" />
                <span>Spouse</span>
              </div>
            </SelectItem>
            <SelectItem value="Partner">
              <div className="flex items-center gap-2">
                <Heart className="h-4 w-4 text-rose-500" />
                <span>Partner</span>
              </div>
            </SelectItem>
            <SelectItem value="Friend">
              <div className="flex items-center gap-2">
                <UserRound className="h-4 w-4 text-emerald-500" />
                <span>Friend</span>
              </div>
            </SelectItem>
            <SelectItem value="Family">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-indigo-500" />
                <span>Family</span>
              </div>
            </SelectItem>
            <SelectItem value="Parent">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-indigo-500" />
                <span>Parent</span>
              </div>
            </SelectItem>
            <SelectItem value="Sibling">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-indigo-500" />
                <span>Sibling</span>
              </div>
            </SelectItem>
            <SelectItem value="Colleague">
              <div className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-amber-500" />
                <span>Colleague</span>
              </div>
            </SelectItem>
            <SelectItem value="Client">
              <div className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-amber-500" />
                <span>Client</span>
              </div>
            </SelectItem>
            <SelectItem value="Professional">
              <div className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-amber-500" />
                <span>Professional</span>
              </div>
            </SelectItem>
            <SelectItem value="Mentor">
              <div className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-amber-500" />
                <span>Mentor</span>
              </div>
            </SelectItem>
            <SelectItem value="Other">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-gray-500" />
                <span>Other</span>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="interests">What are they interested in?</Label>
        <Input
          id="interests"
          placeholder="Cooking, hiking, jazz, etc. (comma-separated)"
          value={interests}
          onChange={(e) => setInterests(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          Gentle reminders about what they care about. No pressure to capture everything perfectly.
        </p>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="note">Add a note (optional)</Label>
        <Textarea
          id="note"
          placeholder="Any beautiful details you want to remember"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="resize-none"
          rows={3}
        />
      </div>

      {/* Spouse-specific fields */}
      {relationshipType === "Spouse" && (
        <div className="space-y-4 border-t pt-4">
          <h3 className="font-medium text-sm">Spouse Details</h3>
          
          <div className="space-y-2">
            <Label>Wedding Date (optional)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn("w-full justify-start text-left font-normal", !weddingDate && "text-muted-foreground")}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {weddingDate ? format(weddingDate, "PPP") : "Select wedding date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={weddingDate}
                  onSelect={setWeddingDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label>Marriage Anniversary (optional)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn("w-full justify-start text-left font-normal", !marriageAnniversary && "text-muted-foreground")}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {marriageAnniversary ? format(marriageAnniversary, "PPP") : "Select anniversary date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={marriageAnniversary}
                  onSelect={setMarriageAnniversary}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label htmlFor="love-language">Love Language (optional)</Label>
            <Select value={loveLanguage} onValueChange={setLoveLanguage}>
              <SelectTrigger>
                <SelectValue placeholder="Select love language" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Words of Affirmation">Words of Affirmation</SelectItem>
                <SelectItem value="Quality Time">Quality Time</SelectItem>
                <SelectItem value="Receiving Gifts">Receiving Gifts</SelectItem>
                <SelectItem value="Acts of Service">Acts of Service</SelectItem>
                <SelectItem value="Physical Touch">Physical Touch</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="spouse-preferences">Preferences & Notes (optional)</Label>
            <Textarea
              id="spouse-preferences"
              placeholder="Things they love, preferences, special memories..."
              value={spousePreferences}
              onChange={(e) => setSpousePreferences(e.target.value)}
              className="resize-none"
              rows={2}
            />
          </div>
        </div>
      )}

      {/* Partner-specific fields */}
      {relationshipType === "Partner" && (
        <div className="space-y-4 border-t pt-4">
          <h3 className="font-medium text-sm">Partner Details</h3>
          
          <div className="space-y-2">
            <Label>First Date (optional)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn("w-full justify-start text-left font-normal", !firstDate && "text-muted-foreground")}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {firstDate ? format(firstDate, "PPP") : "Select first date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={firstDate}
                  onSelect={setFirstDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label htmlFor="anniversary-type">Anniversary Type (optional)</Label>
            <Select value={anniversaryType} onValueChange={setAnniversaryType}>
              <SelectTrigger>
                <SelectValue placeholder="Select anniversary type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Dating">Dating Anniversary</SelectItem>
                <SelectItem value="First Met">First Met</SelectItem>
                <SelectItem value="Relationship">Relationship Anniversary</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Anniversary Date (optional)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn("w-full justify-start text-left font-normal", !anniversaryDate && "text-muted-foreground")}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {anniversaryDate ? format(anniversaryDate, "PPP") : "Select anniversary date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={anniversaryDate}
                  onSelect={setAnniversaryDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label htmlFor="partner-love-language">Love Language (optional)</Label>
            <Select value={loveLanguage} onValueChange={setLoveLanguage}>
              <SelectTrigger>
                <SelectValue placeholder="Select love language" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Words of Affirmation">Words of Affirmation</SelectItem>
                <SelectItem value="Quality Time">Quality Time</SelectItem>
                <SelectItem value="Receiving Gifts">Receiving Gifts</SelectItem>
                <SelectItem value="Acts of Service">Acts of Service</SelectItem>
                <SelectItem value="Physical Touch">Physical Touch</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
      
      <Button type="submit" className="w-full">
        Save Contact
      </Button>
    </form>
  );
};

export default FirstContactForm;
